import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { BaseQLClient } from '@adornis/baseql/client/baseqlClient.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { catchError, concatMap, filter, finalize, from, interval, map, race, Subject, take, tap } from 'rxjs';
import { Answer } from '../../db/answer.js';
import { ILSWebmFile } from '../../db/ils-files.js';

interface QueueItem {
  slot: string;
  type: 'combined' | 'trainee';
  files: File[];
  onSuccess: (fileID: string) => void;
  onError: (e: any) => void;
  tries?: number;
  fileID?: string;
  lastError?: string;
}

export class AnswerUploader {
  private readonly queue = new Subject<QueueItem>();
  private readonly slotsProcessing = new Set<string>();
  constructor({ maxRetries = 10, timeout = 60000 }: { maxRetries?: number; timeout?: number } = {}) {
    // TODO does this subscription need to be cleaned up?
    this.queue
      .pipe(
        concatMap(item => {
          let requestTimedOut = false;
          const { controller, start } = this.uploadItem(item);
          return race<Array<Maybe<QueueItem>>>(
            from(start()).pipe(
              finalize(() => {
                if (requestTimedOut) {
                  controller.abort();
                  item.tries = (item.tries ?? 0) + 1;
                  logger.info({ item }, 'item upload timed out');
                  if (item.tries === maxRetries)
                    return logger.warn({ item }, 'item failed too often, givin up on retrying');
                  this.queue.next(item);
                }
              }),
            ),
            interval(timeout).pipe(
              tap(() => (requestTimedOut = true)),
              map(() => undefined),
            ),
          ).pipe(take(1));
        }),
        filter(Boolean),
        catchError((e, caught) => {
          logger.error({ error: e, caught }, 'Error in AnswerUploader queue');
          return caught;
        }),
      )
      .subscribe(item => {
        if (item.lastError) {
          logger.warn({ item }, 'item upload errored, see lastError property');
        } else {
          logger.debug({ item }, 'item upload completed successfully');
        }
        if (item.fileID) return item.onSuccess(item.fileID);
        item.onError(new Error(`Failed to upload item: ${item.lastError}`));
      });
  }

  public hasSeen(slotId: string) {
    return this.slotsProcessing.has(slotId);
  }

  public async saveAnswerForSlot(slot: Slot, type: 'combined' | 'trainee', files: File[]) {
    const internalID = slot._id + '_' + type;
    if (this.slotsProcessing.has(internalID))
      return logger.warn(
        { slotId: internalID },
        'AnswerUploader.saveAnswerForSlot: Already got files for slot, ignoring this call',
      );
    this.slotsProcessing.add(internalID);
    if (!slot.answer) {
      try {
        slot.answer = new Answer({ type: 'voice', fileId: 'pending', onlyTraineeFileId: 'pending', slotID: slot._id });
        await slot.answer.create();
      } catch (e) {
        logger.error(e, 'Error when trying to create new Answer.');
      }
    }
    return this.enqueueFile(slot._id, type, files);
  }

  public async enqueueFile(slot: string, type: 'combined' | 'trainee', files: File[]) {
    return new Promise<string>((resolve, reject) =>
      this.queue.next({ slot, type, files, onSuccess: resolve, onError: reject }),
    );
  }

  private uploadItem(item: QueueItem) {
    const controller = new AbortController();
    const formData = new FormData();
    let index = 0;
    formData.append(
      '_file_additional_info',
      JSON.stringify({
        sessionID: BaseQLClient.sessionID,
        answerForSlot: item.slot,
        answerType: item.type,
        recordingID: item.slot + '_' + item.type,
      }),
    );
    for (const file of item.files) formData.append('' + index++, file);
    const start = () =>
      fetch(A.absoluteUrl(`/api/file/${ILSWebmFile._class}/upload`), {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      })
        .then(response => response.json())
        .then(({ status, id, detail, message, stack }) => {
          if (status !== 'success') {
            item.lastError = `${detail} ${message} ${stack}`;
            return item;
          }
          item.fileID = id;
          return item;
        })
        .catch(e => {
          logger.error(e, "Error in 'uploadItem'.");
          throw new Error(e);
        });
    return { controller, start };
  }
}
